import React from 'react';
import { Box } from '@mui/material';
import {
  signalBordersLight,
  signalColorsOnLight,
  signalDawLogos,
  signalFonts,
  signalGradients,
  signalRadii,
  signalShadowsLight,
  signalStatusLight,
  signalSurfacesLight,
  signalTextLight,
} from '@/theme/foundations/tokens';
import {
  DawTile,
  InitialsAvatar,
  LogoMark,
  Mono,
  ProgressTrack,
  ScreenFrame,
  StatusPill,
  Waveform,
} from '@/common/components/signal';
import { lightPlayerWave, lightProjects, lightVersions } from '../fixtures';

const NAV: { label: string; count?: string; accent?: boolean }[] = [
  { label: 'Projects', count: '18', accent: true },
  { label: 'Shared with me' },
  { label: 'Stems' },
  { label: 'Boards' },
  { label: 'Transfers', count: '3' },
];

const FILTERS: [string, boolean][] = [
  ['ALL', true],
  ['NEEDS SYNC', false],
  ['SHARED', false],
];

/** File tree rows. `depth` drives the indent; the design hard-codes 3 levels. */
interface TreeRow {
  label: string;
  depth: 0 | 1 | 2;
  /** Folder glyph colour, or the accent note glyph on the playing render. */
  glyph: string;
  glyphColor: string;
  size: string;
  caret?: boolean;
  selected?: boolean;
  badge?: string;
  badgeStatus?: 'syncing' | 'behind';
  color?: string;
}

const tree: TreeRow[] = [
  {
    label: 'Nightdrive Project',
    depth: 0,
    glyph: '▣',
    glyphColor: signalColorsOnLight.accent,
    size: '1.84 GB',
    caret: true,
  },
  {
    label: 'Samples',
    depth: 1,
    glyph: '▣',
    glyphColor: signalColorsOnLight.cyan,
    size: '1.21 GB · 286',
    caret: true,
    color: signalTextLight.body,
  },
  {
    label: 'Imported',
    depth: 2,
    glyph: '▤',
    glyphColor: signalColorsOnLight.cyan,
    size: '874 MB',
    color: signalTextLight.secondary,
  },
  {
    label: 'Renders',
    depth: 1,
    glyph: '▣',
    glyphColor: signalColorsOnLight.cyan,
    size: '302 MB · 9',
    caret: true,
    color: signalTextLight.body,
  },
  {
    label: 'rough master v14.wav',
    depth: 2,
    glyph: '♪',
    glyphColor: signalColorsOnLight.accentFill,
    size: '62.4 MB',
    selected: true,
    badge: 'PLAYING',
    badgeStatus: 'syncing',
  },
  {
    label: 'Nightdrive.als',
    depth: 1,
    glyph: '▤',
    glyphColor: signalColorsOnLight.accent,
    size: '18.4 MB',
    badge: 'LOCAL NEWER',
    badgeStatus: 'behind',
    color: signalTextLight.body,
  },
];

const INDENT: Record<TreeRow['depth'], string> = {
  0: '7px 10px',
  1: '7px 10px 7px 30px',
  2: '7px 10px 7px 52px',
};

const TICKS = ['00:00', '01:00', '02:00', '03:00', '03:58'];

/** Light-surface card. Every panel on this screen is one of these. */
const LightCard: React.FC<{
  children: React.ReactNode;
  padding?: string;
  sx?: object;
}> = ({ children, padding, sx }) => (
  <Box
    sx={{
      border: `1px solid ${signalBordersLight.medium}`,
      borderRadius: `${signalRadii['2xl']}px`,
      background: signalSurfacesLight.panel,
      padding,
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * 11 — Light mode.
 *
 * Not a theme toggle so much as a proof: the same two screens, rebuilt from a
 * light token set with no change to the layout, the type scale or the status
 * vocabulary. The interesting part is what *had* to change — the accent splits
 * into a fill tone and a darker text tone, the waveform's unplayed bars flip
 * from white-alpha to black-alpha, and every shadow loses two thirds of its
 * opacity. Everything else is the same component tree as screens 01 and 02.
 */
export const Screen11LightMode: React.FC = () => (
  <ScreenFrame
    index="11"
    title="Light mode — grid & detail"
    note="SAME TOKENS, LIGHT SET"
    bare
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Projects grid, light */}
      <Box
        sx={{
          width: 1420,
          maxWidth: '100%',
          height: 760,
          display: 'flex',
          borderRadius: `${signalRadii['3xl']}px`,
          overflow: 'hidden',
          border: `1px solid ${signalBordersLight.strong}`,
          background: signalSurfacesLight.canvas,
          color: signalTextLight.primary,
          boxShadow: signalShadowsLight.screen,
        }}
      >
        <Box
          component="aside"
          sx={{
            width: 224,
            flex: 'none',
            background: signalSurfacesLight.panel,
            borderRight: `1px solid ${signalBordersLight.divider}`,
            padding: '20px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '22px',
          }}
        >
          <LogoMark light sx={{ padding: '0 6px' }} />

          <Box
            component="button"
            type="button"
            sx={{
              cursor: 'pointer',
              border: 0,
              fontFamily: signalFonts.ui,
              fontWeight: 600,
              fontSize: '13px',
              color: signalColorsOnLight.onAccent,
              background: signalColorsOnLight.accentFill,
              padding: '10px 14px',
              borderRadius: `${signalRadii.md}px`,
              boxShadow: signalShadowsLight.accent,
              '&:hover': { background: signalColorsOnLight.accentFillHover },
            }}
          >
            ＋ Upload session
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <Mono
              size={10}
              weight={600}
              letterSpacing="0.12em"
              color={signalTextLight.faint}
              sx={{ padding: '6px 12px' }}
            >
              LIBRARY
            </Mono>
            {NAV.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '9px 12px',
                  borderRadius: `${signalRadii.md}px`,
                  fontSize: '13px',
                  ...(item.accent
                    ? {
                        background: 'rgba(240,86,11,0.1)',
                        color: signalTextLight.primary,
                        fontWeight: 600,
                      }
                    : {
                        color: signalTextLight.secondary,
                        cursor: 'pointer',
                        '&:hover': {
                          background: signalSurfacesLight.fillStrong,
                          color: signalTextLight.primary,
                        },
                      }),
                }}
              >
                {/* The active rail is a filled bar; inactive rows keep a
                    transparent one so all five labels align. */}
                <Box
                  component="span"
                  sx={{
                    width: 5,
                    height: 16,
                    borderRadius: '3px',
                    background: item.accent
                      ? signalColorsOnLight.accentFill
                      : 'transparent',
                  }}
                />
                {item.label}
                {item.count && (
                  <Mono
                    size={item.accent ? 11 : 10}
                    weight={item.accent ? 500 : 600}
                    color={
                      item.accent
                        ? signalTextLight.muted
                        : signalColorsOnLight.accent
                    }
                    sx={{ marginLeft: 'auto' }}
                  >
                    {item.count}
                  </Mono>
                )}
              </Box>
            ))}
          </Box>

          <LightCard
            padding="14px"
            sx={{
              marginTop: 'auto',
              borderRadius: `${signalRadii.xl}px`,
              display: 'flex',
              flexDirection: 'column',
              gap: '9px',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Mono size={11} color={signalTextLight.secondary}>
                VAULT
              </Mono>
              <Mono size={11} color={signalTextLight.secondary}>
                14.2 / 20 GB
              </Mono>
            </Box>
            <ProgressTrack
              value={71}
              light
              fill={signalGradients.accentBarLight}
            />
            <Box sx={{ fontSize: '11px', color: signalTextLight.muted }}>
              Pro plan · renews Sep 3
            </Box>
          </LightCard>
        </Box>

        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          <Box
            component="header"
            sx={{
              height: 60,
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '0 24px',
              borderBottom: `1px solid ${signalBordersLight.divider}`,
              background: signalSurfacesLight.glassHeader,
              backdropFilter: 'blur(24px)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flex: 1,
                maxWidth: 360,
                background: signalSurfacesLight.panel,
                border: `1px solid ${signalBordersLight.strong}`,
                borderRadius: `${signalRadii.md}px`,
                padding: '8px 12px',
              }}
            >
              <Box
                component="span"
                sx={{ color: signalTextLight.muted, fontSize: '13px' }}
              >
                ⌕
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: '13px',
                  color: signalTextLight.muted,
                  flex: 1,
                }}
              >
                Search projects, stems, versions…
              </Box>
              <Mono size={11} color={signalTextLight.faint}>
                ⌘K
              </Mono>
            </Box>

            <Box
              sx={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <StatusPill tone={signalStatusLight.synced} dot size={10}>
                DESKTOP AGENT CONNECTED
              </StatusPill>
              <InitialsAvatar
                initials="MK"
                bg={signalColorsOnLight.accentFill}
                fg={signalColorsOnLight.onAccent}
                size={28}
              />
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: 'hidden',
              padding: '26px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: '20px',
              }}
            >
              <Box>
                <Box
                  component="h3"
                  sx={{
                    margin: 0,
                    fontFamily: signalFonts.display,
                    fontWeight: 800,
                    fontSize: '34px',
                    letterSpacing: '-0.03em',
                    lineHeight: 1,
                  }}
                >
                  Projects
                </Box>
                <Box
                  component="p"
                  sx={{
                    margin: '6px 0 0',
                    fontSize: '13px',
                    color: signalTextLight.secondary,
                  }}
                >
                  18 sessions · 3 need attention
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {FILTERS.map(([label, active]) => (
                  <Box
                    component="span"
                    key={label}
                    sx={{
                      padding: '7px 12px',
                      borderRadius: '999px',
                      fontFamily: signalFonts.mono,
                      fontWeight: 600,
                      fontSize: '11px',
                      cursor: 'pointer',
                      ...(active
                        ? {
                            background: signalSurfacesLight.panel,
                            border: `1px solid ${signalBordersLight.control}`,
                            color: signalTextLight.primary,
                          }
                        : {
                            border: `1px solid ${signalBordersLight.medium}`,
                            color: signalTextLight.muted,
                            '&:hover': { color: signalTextLight.primary },
                          }),
                    }}
                  >
                    {label}
                  </Box>
                ))}
                <Box
                  component="span"
                  sx={{
                    width: '1px',
                    height: 20,
                    background: signalBordersLight.strong,
                    margin: '0 4px',
                  }}
                />
                <Mono size={11} color={signalTextLight.muted}>
                  SORT · MODIFIED
                </Mono>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px',
              }}
            >
              {lightProjects.map((project) => (
                <LightCard
                  key={project.name}
                  sx={{
                    overflow: 'hidden',
                    boxShadow: signalShadowsLight.card,
                    '&:hover': {
                      borderColor: signalBordersLight.accentHover,
                    },
                  }}
                >
                  <Box
                    sx={{
                      padding: '14px 14px 10px',
                      display: 'flex',
                      gap: '11px',
                      alignItems: 'flex-start',
                    }}
                  >
                    <DawTile
                      src={project.logo}
                      label={project.daw}
                      light
                      size={36}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          fontFamily: signalFonts.display,
                          fontWeight: 700,
                          fontSize: '16px',
                          letterSpacing: '-0.015em',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {project.name}
                      </Box>
                      <Mono
                        size={10.5}
                        color={signalTextLight.muted}
                        sx={{ display: 'block', marginTop: '3px' }}
                      >
                        {project.meta}
                      </Mono>
                    </Box>
                    <StatusPill
                      tone={signalStatusLight[project.status]}
                      dot
                      size={9.5}
                    >
                      {project.statusLabel}
                    </StatusPill>
                  </Box>

                  <Waveform
                    bars={project.wave}
                    height={56}
                    sx={{ padding: '0 14px' }}
                  />

                  <Box
                    sx={{
                      padding: '11px 14px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderTop: `1px solid ${signalBordersLight.subtle}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {project.people.map((person, i) => (
                        <InitialsAvatar
                          key={`${person.initials}-${i}`}
                          {...person}
                          size={22}
                          ring={signalSurfacesLight.avatarRing}
                          sx={{ marginRight: '-7px' }}
                        />
                      ))}
                    </Box>
                    <Mono size={10.5} color={signalTextLight.muted}>
                      {project.updated}
                    </Mono>
                  </Box>
                </LightCard>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Project detail, light */}
      <Box
        sx={{
          width: 1420,
          maxWidth: '100%',
          borderRadius: `${signalRadii['3xl']}px`,
          overflow: 'hidden',
          border: `1px solid ${signalBordersLight.strong}`,
          background: signalSurfacesLight.canvas,
          color: signalTextLight.primary,
          boxShadow: signalShadowsLight.screen,
          padding: '22px 26px 26px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            alignItems: 'flex-start',
            marginBottom: '20px',
          }}
        >
          <DawTile
            src={signalDawLogos.Ableton}
            label="Ableton Live"
            light
            size={52}
            radius={12}
          />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Box
                component="h3"
                sx={{
                  margin: 0,
                  fontFamily: signalFonts.display,
                  fontWeight: 800,
                  fontSize: '30px',
                  letterSpacing: '-0.03em',
                  lineHeight: 1,
                }}
              >
                Nightdrive
              </Box>
              <StatusPill tone={signalStatusLight.neutral} size={10}>
                v14
              </StatusPill>
              <StatusPill tone={signalStatusLight.synced} dot size={10}>
                SYNCED
              </StatusPill>
            </Box>
            <Mono
              size={11}
              color={signalTextLight.muted}
              sx={{ display: 'block', marginTop: '7px' }}
            >
              ABLETON LIVE 12 · 128 BPM · Fm · 1.84 GB · 412 FILES
            </Mono>
          </Box>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <Box
              component="button"
              type="button"
              sx={{
                cursor: 'pointer',
                fontFamily: signalFonts.ui,
                fontWeight: 600,
                fontSize: '13px',
                color: signalTextLight.primary,
                background: signalSurfacesLight.panel,
                border: `1px solid ${signalBordersLight.control}`,
                padding: '9px 15px',
                borderRadius: `${signalRadii.md}px`,
                '&:hover': { background: signalSurfacesLight.iconWell },
              }}
            >
              Share
            </Box>
            <Box
              component="button"
              type="button"
              sx={{
                border: 0,
                cursor: 'pointer',
                fontFamily: signalFonts.ui,
                fontWeight: 600,
                fontSize: '13px',
                color: signalColorsOnLight.onAccent,
                background: signalColorsOnLight.accentFill,
                padding: '9px 16px',
                borderRadius: `${signalRadii.md}px`,
                '&:hover': { background: signalColorsOnLight.accentFillHover },
              }}
            >
              Open in Ableton
            </Box>
          </Box>
        </Box>

        <LightCard
          padding="18px"
          sx={{
            borderColor: signalBordersLight.card,
            boxShadow: signalShadowsLight.panel,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              marginBottom: '14px',
            }}
          >
            <Box
              component="button"
              type="button"
              aria-label="Pause"
              sx={{
                width: 44,
                height: 44,
                flex: 'none',
                borderRadius: '999px',
                border: 0,
                cursor: 'pointer',
                background: signalColorsOnLight.accentFill,
                color: signalColorsOnLight.onAccent,
                fontSize: '15px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: signalShadowsLight.accentLg,
                '&:hover': { background: signalColorsOnLight.accentFillHover },
              }}
            >
              ❚❚
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontWeight: 600, fontSize: '14px' }}>
                Nightdrive — rough master v14.wav
              </Box>
              <Mono
                size={11}
                color={signalTextLight.muted}
                sx={{ display: 'block', marginTop: '3px' }}
              >
                RENDERED 4 MIN AGO · 48 kHz / 24-bit · 62.4 MB
              </Mono>
            </Box>
            <Mono size={15} weight={600} color={signalTextLight.primary}>
              01:42.06
              <Box
                component="span"
                sx={{ color: signalTextLight.faint, fontSize: '12px' }}
              >
                {' / 03:58.20'}
              </Box>
            </Mono>
          </Box>

          <Box sx={{ position: 'relative', cursor: 'pointer' }}>
            <Waveform bars={lightPlayerWave} height={86} />
            <Box
              sx={{
                position: 'absolute',
                left: '43%',
                top: 0,
                bottom: 0,
                width: '2px',
                background: signalTextLight.primary,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: '43%',
                top: '-6px',
                transform: 'translateX(-50%)',
                width: 10,
                height: 10,
                borderRadius: '999px',
                background: signalTextLight.primary,
              }}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '10px',
            }}
          >
            {TICKS.map((tick) => (
              <Mono key={tick} size={10} color={signalTextLight.faint}>
                {tick}
              </Mono>
            ))}
          </Box>
        </LightCard>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px',
            marginTop: '16px',
          }}
        >
          <LightCard sx={{ overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderBottom: `1px solid ${signalBordersLight.subtle}`,
              }}
            >
              <Mono
                size={11}
                weight={600}
                letterSpacing="0.1em"
                color={signalTextLight.muted}
              >
                FILE TREE · 412 FILES
              </Mono>
              <Mono size={11} color={signalTextLight.muted}>
                1.84 GB
              </Mono>
            </Box>
            <Box
              sx={{
                padding: '8px 10px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '1px',
                fontSize: '13px',
              }}
            >
              {tree.map((row) => (
                <Box
                  key={row.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '9px',
                    padding: INDENT[row.depth],
                    borderRadius: `${signalRadii.sm}px`,
                    color: row.color ?? signalTextLight.primary,
                    ...(row.selected
                      ? {
                          background: 'rgba(240,86,11,0.1)',
                          fontWeight: 600,
                        }
                      : {
                          ...(row.depth === 0 && { fontWeight: 600 }),
                          '&:hover': {
                            background: signalSurfacesLight.fill,
                          },
                        }),
                  }}
                >
                  {row.caret && (
                    <Box
                      component="span"
                      sx={{ color: signalTextLight.muted, fontSize: '10px' }}
                    >
                      ▾
                    </Box>
                  )}
                  <Box component="span" sx={{ color: row.glyphColor }}>
                    {row.glyph}
                  </Box>
                  {row.label}
                  {row.badge && (
                    <StatusPill
                      tone={signalStatusLight[row.badgeStatus ?? 'syncing']}
                      size={9}
                      borderless
                      sx={{ marginLeft: '8px', padding: '2px 7px' }}
                    >
                      {row.badge}
                    </StatusPill>
                  )}
                  <Mono
                    size={11}
                    color={signalTextLight.muted}
                    sx={{ marginLeft: 'auto' }}
                  >
                    {row.size}
                  </Mono>
                </Box>
              ))}
            </Box>
          </LightCard>

          <LightCard padding="16px 18px">
            <Mono
              size={11}
              weight={600}
              letterSpacing="0.1em"
              color={signalTextLight.muted}
              sx={{ display: 'block', marginBottom: '14px' }}
            >
              VERSION HISTORY
            </Mono>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {lightVersions.map((version, i) => {
                const last = i === lightVersions.length - 1;
                return (
                  <Box
                    key={version.tag}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '26px 1fr',
                      gap: '10px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          width: 11,
                          height: 11,
                          borderRadius: '999px',
                          background: version.dot,
                          boxShadow: `0 0 0 4px ${version.ring}`,
                          marginTop: '4px',
                        }}
                      />
                      {/* The rail stops at the last node here — unlike the
                          landing page's timeline, this list is complete. */}
                      {!last && (
                        <Box
                          component="span"
                          sx={{
                            flex: 1,
                            width: '1px',
                            background: signalBordersLight.medium,
                          }}
                        />
                      )}
                    </Box>
                    <Box sx={{ paddingBottom: last ? 0 : '16px' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: '8px',
                        }}
                      >
                        <Mono size={12} weight={600} color={version.dot}>
                          {version.tag}
                        </Mono>
                        <Box sx={{ fontWeight: 600, fontSize: '13px' }}>
                          {version.title}
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          fontSize: '12px',
                          color: signalTextLight.secondary,
                          marginTop: '4px',
                          lineHeight: 1.5,
                        }}
                      >
                        {version.note}
                      </Box>
                      <Mono
                        size={10.5}
                        color={signalTextLight.muted}
                        sx={{ display: 'block', marginTop: '6px' }}
                      >
                        {version.meta}
                      </Mono>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </LightCard>
        </Box>
      </Box>
    </Box>
  </ScreenFrame>
);

export default Screen11LightMode;
